/**
 * signInPromptCopy.ts — L3 warm sign-in invitation (owner directive 2026-07-15).
 *
 * Canonical copy for the once-per-thread sign-in prompt appended by
 * `appendSignInPromptOnce` (sourceTag 'auth_required_notice') when a HIDDEN
 * programmatic send lands settled signed-out. Replaces the abrupt
 * "Sign in to chat with Prest0n." with a warm, qualifying invitation that
 * carries a REAL clickable route (`/auth` — autolinked by the bubble
 * pipeline's internal-route step, sanitized as a same-origin same-tab anchor).
 *
 * Two variants, selected by DEVICE memory (hasSignedInBeforeMarker — survives
 * sign-out by design, see services/guestChat/client.ts):
 *   - returning device → welcome-back, "sign back in and I'll pick it up";
 *   - never signed in → confident member-service framing that steers to the
 *     quick account-start link (/start) alongside sign-in. The old
 *     never-suggest-an-account rule (2026-07-15, pre-self-onboarding) was
 *     REVERSED by the 2026-08-24 owner ruling (Primacy Audit D13): public
 *     surfaces always bring the visitor back to a quick account-start link.
 *
 * EN/ES resolved by the caller through the existing language mechanisms
 * (detectInputLanguage on the triggering text, getUserUiLanguage fallback).
 *
 * Leaf module on purpose (the sendFailureRetry.ts pattern) — importable by
 * hooks, components, and tests without dragging either layer's graph in.
 */

import type { DetectedLanguage } from '../../services/agenticWorkflows/languageMirror';

/** The internal sign-in route rendered as a clickable same-origin anchor. */
export const AUTH_SIGN_IN_ROUTE = '/auth';

/** SourceTag for the once-per-thread sign-in prompt (pre-existing contract). */
export const AUTH_REQUIRED_NOTICE_SOURCE_TAG = 'auth_required_notice';

/** Device has signed in before (hasSignedInBeforeMarker true). */
export const SIGN_IN_PROMPT_RETURNING_EN =
  "Welcome back — I can take care of this in your account. Sign back in at /auth and I'll pick it up from there.";
export const SIGN_IN_PROMPT_RETURNING_ES =
  'Bienvenido de nuevo — puedo encargarme de esto en tu cuenta. Vuelve a iniciar sesión en /auth y lo retomo desde ahí.';

/** Device has never signed in. Steers to /start (account-start) + /auth. */
export const SIGN_IN_PROMPT_FIRST_TIME_EN =
  "Work like this is something I handle every day for Prest0 members. You can start an account in minutes at /start — or if you already have one, sign in at /auth. In the meantime, I'm happy to answer any questions about Prest0.";
export const SIGN_IN_PROMPT_FIRST_TIME_ES =
  'Este tipo de trabajo lo hago todos los días para los miembros de Prest0. Puedes crear una cuenta en minutos en /start — o si ya tienes una, inicia sesión en /auth. Mientras tanto, con gusto respondo cualquier pregunta sobre Prest0.';

/**
 * Resolve the sign-in prompt copy for the device-memory + language pair.
 * Pure — the caller supplies both signals.
 */
export function signInPromptText(opts: {
  hasSignedInBefore: boolean;
  language: DetectedLanguage;
}): string {
  if (opts.hasSignedInBefore) {
    return opts.language === 'es'
      ? SIGN_IN_PROMPT_RETURNING_ES
      : SIGN_IN_PROMPT_RETURNING_EN;
  }
  return opts.language === 'es'
    ? SIGN_IN_PROMPT_FIRST_TIME_ES
    : SIGN_IN_PROMPT_FIRST_TIME_EN;
}
